import type { ApolloQueryResult, ApolloClient } from 'apollo-client';
import type { DocumentNode, GraphQLError } from 'graphql';
import { SSRMeasures } from '@confluence/action-measures';
import { getMonitoringClient } from '@confluence/monitoring';

import type { SessionDataType } from './SessionDataTypes';
import { SessionDataQuery } from './SessionDataQuery.graphql';
import { FeatureFlagsQuery } from './FeatureFlagsQuery.graphql';
import type { SessionDataQuery as SessionDataQueryType } from './__types__/SessionDataQuery';
import type { FeatureFlagsQuery as FeatureFlagsQueryType } from './__types__/FeatureFlagsQuery';

declare global {
	interface Window {
		__SESSION_DATA_PROMISE__: Promise<SessionDataType> | null;
		__SESSION_DATA__:
			| Promise<(SessionDataQueryType & FeatureFlagsQueryType) | null>
			| (SessionDataQueryType & FeatureFlagsQueryType)
			| Promise<null>
			| null;
		__LD_FEATURE_FLAGS__?: FeatureFlagsQueryType;
	}
}

function apolloQuery<Q>({
	query,
	queryName,
	client = window.GLOBAL_APOLLO_CLIENT,
}: {
	query: DocumentNode;
	queryName: string;
	client?: ApolloClient<any>;
}): Promise<{ data: Q | null }> {
	if (!client) {
		return Promise.reject(new Error(`ApolloClient not available, cannot load query ${queryName}`));
	}

	return SSRMeasures.run(`ssr-app/prepare/preloadQuery/fetch:${queryName}`, async () =>
		client.query({
			query,
			errorPolicy: 'all',
			fetchPolicy: 'cache-first',
			context: {
				single: false,
				allowOnExternalPage: true,
			},
		}),
	);
}

function writeQuery<TData>(options: {
	query: DocumentNode;
	data: TData;
	client?: ApolloClient<any>;
}) {
	const client = options.client || window.GLOBAL_APOLLO_CLIENT;
	if (client) {
		client.writeQuery(options);
	}
}

export async function loadSessionData(): Promise<
	(SessionDataQueryType & FeatureFlagsQueryType) | null
> {
	// __SESSION_DATA__ is either actual session data
	// or the promise that is resolving the session data
	// In the case when page is rendered by SSR, the __SESSION_DATA__ will be populated with JSON data.
	if (window['__SESSION_DATA__']) {
		return window['__SESSION_DATA__'];
	}

	const queries: any = [];
	queries.push(
		apolloQuery<SessionDataQueryType | null>({
			query: SessionDataQuery,
			queryName: 'SessionDataQuery',
		}),
	);

	if (window['__LD_FEATURE_FLAGS__']) {
		writeQuery<FeatureFlagsQueryType>({
			query: FeatureFlagsQuery,
			data: window['__LD_FEATURE_FLAGS__'],
		});
		queries.push({
			data: window['__LD_FEATURE_FLAGS__'],
		});
	} else {
		queries.push(
			apolloQuery<FeatureFlagsQueryType | null>({
				query: FeatureFlagsQuery,
				queryName: 'FeatureFlagsQuery',
			}),
		);
	}

	window['__SESSION_DATA__'] = Promise.all(queries)
		.then((queriesRes) => {
			const sessionDataQueryResult =
				queriesRes[0] as ApolloQueryResult<SessionDataQueryType | null>;
			const featureFlagsQueryResult =
				queriesRes[1] as ApolloQueryResult<FeatureFlagsQueryType | null>;

			const errors: GraphQLError[] = ([] as GraphQLError[]).concat(
				sessionDataQueryResult.errors ?? [],
				featureFlagsQueryResult.errors ?? [],
			);

			if (errors.length > 0) {
				throw errors;
			}

			let result: (SessionDataQueryType & FeatureFlagsQueryType) | null = null;

			if (sessionDataQueryResult.data && featureFlagsQueryResult.data) {
				result = {
					...sessionDataQueryResult.data,
					...featureFlagsQueryResult.data,
				};
			}
			return result;
		})
		.catch((error) => {
			if (Array.isArray(error)) {
				error.forEach((e) => getMonitoringClient().submitError(e, { attribution: 'FATAL' }));
			} else {
				getMonitoringClient().submitError(error, { attribution: 'FATAL' });
			}

			return null;
		});

	return window['__SESSION_DATA__'];
}
